import {Injectable} from "@angular/core";
import {Apollo} from "apollo-angular";
import {
  BulkOrderDataGqlModel, BulkOrderDetailsMutateGqlModel,
  BulkOrderGqlModel,
  BulkOrderListGqlModel,
  BulkOrderMutateGqlModel,
  BulkOrderRemoveGqlModel,
  BulkOrderUpdateModel,
  ConfirmBulkOrderPlacedGqlModel,
  RequestBulkOrderPlaceGqlModel,
} from "./bulk-order.gql-model";
import {sequenceAsync} from "../misc/sequence.decorator";
import {map} from "rxjs/operators";
import {OrderFormGqlModel} from "../order/order-form.gql-model";
import {HttpClient} from "@angular/common/http";
import {HardwareOrderDetailsInputModel} from "../order/order.gql-model";

const DEFAULT_PAGE = "1";
const DEFAULT_PAGE_LIMIT = "10";

@Injectable()
export class BulkOrderService {
  constructor(private apollo: Apollo, private http: HttpClient) {}

  getById(id): Promise<BulkOrderGqlModel> {
    const query = BulkOrderDataGqlModel.query;
    const variables = BulkOrderDataGqlModel.variables(id);

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(
        BulkOrderDataGqlModel.mapper,
        map((order: BulkOrderDataGqlModel) => order.bulkOrder || new BulkOrderGqlModel({})),
      )
      .toPromise();
  }

  @sequenceAsync()
  list(
    projectId: string = null,
    search: string = null,
    page: string = DEFAULT_PAGE,
    limit: string = DEFAULT_PAGE_LIMIT,
  ): Promise<BulkOrderListGqlModel> {
    const query = BulkOrderListGqlModel.query;
    const variables = BulkOrderListGqlModel.variables(projectId, search, +page, +limit);

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(BulkOrderListGqlModel.mapper)
      .toPromise();
  }

  create(name: string, projectId: string): Promise<BulkOrderGqlModel> {
    const bulkOrder = new BulkOrderGqlModel({name, projectId});

    return this.save(bulkOrder);
  }

  save(bulkOrder: BulkOrderGqlModel): Promise<BulkOrderGqlModel> {
    const model = new BulkOrderMutateGqlModel(new BulkOrderUpdateModel(bulkOrder));

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(BulkOrderMutateGqlModel.mapper)
      .toPromise();
  }

  confirmOrderForm(id: string): Promise<BulkOrderGqlModel> {
    const model = new BulkOrderMutateGqlModel(new RequestBulkOrderPlaceGqlModel(id));

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(BulkOrderMutateGqlModel.mapper)
      .toPromise();
  }

  confirmOrderPlaced(id: string): Promise<BulkOrderGqlModel> {
    const model = new BulkOrderMutateGqlModel(new ConfirmBulkOrderPlacedGqlModel(id));

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(BulkOrderMutateGqlModel.mapper)
      .toPromise();
  }

  remove(id: string): Promise<BulkOrderGqlModel> {
    const model = new BulkOrderMutateGqlModel(new BulkOrderRemoveGqlModel(id));

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(BulkOrderMutateGqlModel.mapper)
      .toPromise();
  }

  getOrderForm(id: string): Promise<OrderFormGqlModel> {
    const query = BulkOrderDataGqlModel.query;
    const variables = BulkOrderDataGqlModel.variables(id, true);

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(
        BulkOrderDataGqlModel.mapper,
        map((order: BulkOrderDataGqlModel) => order.orderForm || new OrderFormGqlModel({})),
      )
      .toPromise();
  }

  updateOrderNumbersAndDates(bulkOrderId: string, details: HardwareOrderDetailsInputModel[]): Promise<boolean> {
    const model = new BulkOrderDetailsMutateGqlModel({bulkOrderId, details});

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(
        BulkOrderDetailsMutateGqlModel.mapper,
        map(result => result.success),
      )
      .toPromise();
  }

  /** @deprecated */
  notifyOrderAcceptance(bulkOrderId: string): Promise<boolean> {
    return this.http.post<{success: boolean}>(`/api/bulk-order-form/request-order-acceptance`, {bulkOrderId})
      .pipe(map(json => !!json["success"]))
      .toPromise();
  }
}
